import memoizeOne from 'memoize-one'
import {
  always,
  both,
  either,
  identity,
  intersection,
  lte,
  partition,
  pick,
  pipe,
  prop,
} from 'ramda'

import { titleize } from 'inflection'

import { activeUserAllowedTypes, defaultAllowedTypes } from '~/src/Annotations/constants'
import { SCOPING_PROP_DEFAULT_VALUE, SCOPING_PROPS } from '~/src/Journal/constants'
import { EPOCH_DATE } from '~/src/Lib/Constants'
import createLogger from '~/src/Lib/Logging'
import safeStorage from '~/src/Lib/safeStorage'
import { EMPTY_OBJECT, getDateTime, shallowEquals } from '~/src/Lib/Utils'

import {
  getCalendarEnd,
  getCalendarStart,
  getStepDays,
  getTopByTimestamp,
  getTopItemIndex,
  isFiltered,
  prepareScope,
} from './selectors'

const localStorage = safeStorage('local')

const logger = createLogger('Journal#reducer')

export const DEFAULT_STATE = {
  activeHarvest: null,
  activeRoom: null,
  activeTypes: defaultAllowedTypes,
  activeUser: null,
  allowedTypes: defaultAllowedTypes,
  autoFetch: true,
  autoScroll: false,
  infiniteLoading: false,
  loaded: false,
  loader: EMPTY_OBJECT,
  loading: false,
  loadMore: null,
  previousLoading: false,
  previousInfiniteLoading: false,
  scrollerIndices: EMPTY_OBJECT,
}
const FROZEN = ['systemAllowedTypes']
const READ_ONLY = {
  allowedTypes: 1,
  today: 1,
  typeToSchema: 1,
}

export const CUSTOM_ACTIONS = {
  patch: Symbol.for('JOURNAL_STATE_PATCH'),
  goToNextMonth: Symbol.for('JOURNAL_CALENDAR_GO_TO_NEXT_MONTH'),
  goToPrevMonth: Symbol.for('JOURNAL_CALENDAR_GO_TO_PREV_MONTH'),
}

const tsToDateTime = pipe(prop('timestamp'), getDateTime)
const getClosestToSelectedDate = (journal, selectedDate) => {
  const { after: afterItems, before: beforeItems, items } = journal
  const filtered = (afterItems && beforeItems ? items : items.slice(
    // if there are no items before current, start from second index
    beforeItems ? 0 : 1,
    // if there are no items after current, end at second to last item
    afterItems ? items.length : -1
  )).filter(prop('timestamp'))
  const [after, before] = partition(pipe(tsToDateTime, lte(selectedDate)), filtered)
  const [lastBefore] = before.filter(Boolean).slice(-1)
  const [firstAfter] = after.filter(Boolean)
  const lbDiff = lastBefore ? Math.abs(selectedDate.diff(getDateTime(lastBefore.timestamp)).as('milliseconds')) : Infinity
  const faDiff = firstAfter ? Math.abs(selectedDate.diff(getDateTime(firstAfter.timestamp)).as('milliseconds')) : Infinity

  return (lbDiff > faDiff) ? (firstAfter ?? lastBefore) : (lastBefore ?? firstAfter)
}
const activeUserAllowedInstersection = pipe(
  // get the intersection of the activeUser allowed types
  intersection(activeUserAllowedTypes),
  // if it has a non-zero length, use the intersection, else activeUser allowed types
  either(
    both(prop('length'), identity),
    always(activeUserAllowedTypes)
  )
)

const trackPrevious = (previousState, currentState, action) => {
  const { type } = action
  const { [type]: previous } = previousState
  const { [type]: current } = currentState

  if (previous === current) {
    return currentState
  }

  return {
    ...currentState,
    [`previous${titleize(type)}`]: previous
  }
}

export const trackPreviousHandler = handler => (state, action) => trackPrevious(state, handler(state, action), action)

export const autoFetchOnChangeHandler = (handler, key, opts = { replace: false }) => (state, action) => {
  const { [key]: oldValue } = state
  const newState = handler(state, action)
  const { [key]: newValue } = newState
  if (oldValue !== newValue) {
    newState.autoFetch = true
    newState.loaded = !opts.replace
  }
  return newState
}

export const defaultActionHandler = (state, action, readOnly = READ_ONLY) => {
  const { type: key, payload } = action

  if (key in readOnly) return state

  if (key in state) {
    const { [key]: previousValue } = state
    let value = payload
    if (typeof payload === 'function') {
      value = payload(previousValue)
    }

    if (typeof previousValue === 'object' && shallowEquals(previousValue, value)) {
      return state
    }

    if (previousValue !== value) {
      return {
        ...state,
        [key]: value
      }
    }
  } else if (localStorage.debug === 'true') {
    // eslint-disable-next-line no-console
    console.warn('invalid action dispatched to useJournalState reducer:', action)
  }

  return state
}

export const journalHandler = (state, action) => {
  const { payload: journal } = action
  const { journal: oldJournal, selectedDate, topItem: oldTop } = state

  if (!shallowEquals(journal, oldJournal)) {
    const result = {
      ...state,
      journal,
    }
    const { items: oldItems, loading: oldLoading } = oldJournal
    const { items, loading } = journal

    if (!loading && !oldTop) {
      const topItem = getTopByTimestamp(items, selectedDate)
      result.topItem = topItem
    }

    if (oldLoading && !loading) {
      if (getTopItemIndex(state) !== getTopItemIndex(result) && getTopItemIndex(result) !== -1) {
        result.autoScroll = true
      }
    }

    if (oldItems !== items && result.topItem) {
      if (!items.find(item => result.topItem.uuid === item.uuid)) {
        let topItem = getTopByTimestamp(items, selectedDate)
        if (!topItem) {
          topItem = getClosestToSelectedDate(journal, selectedDate)
        }
        result.topItem = topItem
        result.autoScroll = true
      }
      const oldTopIndex = getTopItemIndex(state)
      const newTopIndex = getTopItemIndex(result)

      result.autoScroll = (
        result.topItem !== oldTop
        || oldTopIndex !== newTopIndex
      )
    }

    return result
  }

  return state
}

export const selectedDateHandler = (state, action) => {
  const {
    autoScroll: oldAutoScroll,
    calendarStartDate: oldStart,
    calendarEndDate: oldEnd,
    journal: { items },
    selectedDate: oldSelected,
    topItem: oldTop,
  } = state
  const { payload: newSelected } = action
  const { updateTopItem = true } = action.meta ?? EMPTY_OBJECT
  let autoScroll = oldAutoScroll

  if (!newSelected.hasSame(oldSelected, 'day')) {
    const selectedDate = newSelected
    let calendarStartDate = oldStart
    let calendarEndDate = oldEnd
    let topItem = oldTop
    // If we've changed months, update calendar dates
    if (!oldStart.until(oldEnd).contains(newSelected)) {
      calendarStartDate = getCalendarStart(newSelected)
      calendarEndDate = getCalendarEnd(newSelected)
    }
    // If we've changed dates without scrolling, update topItem and tell auto-scroll reactor
    // to scroll
    if (updateTopItem) {
      topItem = getTopByTimestamp(items, selectedDate)
      autoScroll = true
    }

    const newState = {
      ...state,
      autoScroll,
      calendarStartDate,
      calendarEndDate,
      selectedDate,
      topItem,
    }

    return newState
  }

  return state
}

export const scrollerIndicesHandler = (state, action) => {
  const { payload: scrollerIndices } = action

  if (!shallowEquals(state.scrollerIndices, scrollerIndices)) {
    const {
      journal: { items, loading: journalLoading, lastFetched },
      loaded,
      loading,
      selectedDate: oldSelected,
      topItem: oldTop,
    } = state
    const now = Date.now()
    const elapsed = now - lastFetched
    if (loading || journalLoading || loading || elapsed < 250) {
      return {
        ...state,
        scrollerIndices,
      }
    }
    const { visibleStartIndex: topIndex } = scrollerIndices
    const { [topIndex]: newTop } = items
    const topItem = newTop ?? oldTop
    const newSelected = newTop?.timestamp ? getDateTime(newTop.timestamp).startOf('day') : oldSelected

    if (newSelected.hasSame(oldSelected, 'day') || !loaded) {
      return {
        ...state,
        scrollerIndices,
        topItem,
      }
    }

    return selectedDateHandler({
      ...state,
      scrollerIndices,
      topItem,
    }, {
      payload: newSelected,
      meta: {
        ...action.meta,
        updateTopItem: false
      }
    })
  }

  return state
}

export const loadMoreHandler = (state, action) => {
  const { deviceCount, selectedDate, journal } = state
  if (journal?.loading || (state.loadMore && state.infiniteLoading && action.payload)) {
    return state
  }
  if (action.payload === null) {
    return {
      ...state,
      loadMore: action.payload
    }
  }

  const { before, after, items, params } = journal
  const { payload: { end: stop, resolve, reject } } = action
  const itemsEnd = (after ?? 0) + (items?.length ?? 0)
  const loadBefore = stop >= itemsEnd
  if ((!loadBefore && !after) || (loadBefore && !before)) {
    if (!state.loadMore) return state
    resolve(true)
    return { ...state, loadMore: null }
  }
  const filtered = isFiltered(prepareScope(state), state)
  const days = getStepDays(filtered, deviceCount)
  const currentStart = getDateTime(params.start ?? selectedDate)
  const currentEnd = getDateTime(params.end ?? selectedDate)

  let start = before < 50 ? getDateTime(EPOCH_DATE) : currentStart.minus({ days })
  let end = currentStart
  if (!loadBefore) {
    start = currentEnd
    end = after < 50 ? currentEnd.plus({ years: 10 }) : currentEnd.plus({ days })
  }
  return {
    ...state,
    loadMore: {
      start,
      end,
      resolve,
      reject,
    }
  }
}

export const actionHandlers = {
  activeTypes: autoFetchOnChangeHandler(defaultActionHandler, 'activeTypes', { replace: true }),
  activeUser: autoFetchOnChangeHandler((state, action) => {
    const { activeUser } = defaultActionHandler(state, action)
    const { activeTypes, activeUser: oldActiveUser } = state
    if (oldActiveUser === activeUser) return state
    if (activeUser) {
      return {
        ...state,
        activeUser,
        activeTypes: !oldActiveUser ? activeUserAllowedInstersection(activeTypes) : activeTypes,
        allowedTypes: activeUserAllowedTypes,
        loaded: false,
      }
    }
    return {
      ...state,
      activeUser,
      calendarStartDate: getCalendarStart(state.today),
      calendarEndDate: getCalendarEnd(state.today),
      allowedTypes: defaultAllowedTypes,
      loaded: false,
      selectedDate: state.today,
    }
  }, 'activeUser', { replace: true }),
  infiniteLoading: trackPreviousHandler(defaultActionHandler),
  journal: journalHandler,
  loading: trackPreviousHandler(defaultActionHandler),
  loadMore: loadMoreHandler,
  scrollerIndices: scrollerIndicesHandler,
  selectedDate: selectedDateHandler,
  [CUSTOM_ACTIONS.goToPrevMonth]: state => {
    const { calendarStartDate: oldStart } = state
    // const calendarStartDate = getCalendarStart(oldStart.minus({ weeks: 2 }))
    // const calendarEndDate = getCalendarEnd(oldStart.minus({ days: 2 }))

    return selectedDateHandler(state, { payload: oldStart.minus({ weeks: 2 }) })
    // return {
    //   ...state,
    //   autoFetch: true,
    //   calendarStartDate,
    //   calendarEndDate,
    // }
  },
  [CUSTOM_ACTIONS.goToNextMonth]: state => {
    const { calendarEndDate: oldEnd } = state
    const calendarStartDate = getCalendarStart(oldEnd.plus({ days: 2 }))
    const calendarEndDate = getCalendarEnd(oldEnd.plus({ weeks: 2 }))

    return {
      ...state,
      autoFetch: true,
      calendarStartDate,
      calendarEndDate,
    }
  }
}

actionHandlers[CUSTOM_ACTIONS.patch] = (state, action) => {
  const { payload: patch, meta } = action
  const allowedKeys = Object.keys(action.payload).filter(key => !(key in READ_ONLY))
  if (shallowEquals(pick(allowedKeys, state), patch)) {
    return state
  }

  return allowedKeys.reduce((newState, type) => (
    (actionHandlers[type] || defaultActionHandler)(newState, { type, payload: patch[type], meta })
  ), state)
}

export const reducer = (state, action) => {
  if (!action?.type) return state
  const nextState = (actionHandlers[action.type] ?? defaultActionHandler)(state, action)
  FROZEN.forEach(key => {
    if (nextState[key] !== state[key]) {
      nextState[key] = state[key]
    }
  })
  return nextState
}

export const getStateInitializer = systemAllowedTypes => props => {
  const {
    activeTypes = DEFAULT_STATE.activeTypes,
    date,
    devices = EMPTY_OBJECT,
    journal = EMPTY_OBJECT
  } = props

  const { items } = journal
  const today = getDateTime('now').startOf('day')
  const selectedDate = date ? getDateTime(date).startOf('day') : today
  const scope = pick(SCOPING_PROPS.filter(key => props[key] !== SCOPING_PROP_DEFAULT_VALUE), props)

  return {
    ...DEFAULT_STATE,
    ...scope,
    activeTypes,
    calendarStartDate: getCalendarStart(selectedDate),
    calendarEndDate: getCalendarEnd(selectedDate),
    deviceCount: Object.keys(devices).length,
    journal,
    selectedDate,
    systemAllowedTypes,
    today,
    topItem: getTopByTimestamp(items, selectedDate),
  }
}

export const getDispatcher = memoizeOne(dispatch => (...args) => {
  if (args.length === 1) {
    if ('type' in args[0]) {
      dispatch(args[0])
    } else {
      dispatch({
        type: CUSTOM_ACTIONS.patch,
        payload: args[0]
      })
    }
    return
  }
  const [type, payload, meta] = args
  dispatch({ type, payload, meta })
})
